import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Check } from 'lucide-react';

// Pricing configuration (easily editable)
const PRICING_TIERS = [
  {
    name: 'Starter',
    price: '$0',
    period: '/ month',
    description: 'Perfect for getting started',
    features: [
      'Up to 50 invoices',
      'Basic reporting',
      'CSV upload',
      'Email support',
    ],
    cta: 'Start Free',
    ctaLink: '/register',
    highlighted: false,
  },
  {
    name: 'Team',
    price: 'Contact us',
    period: '',
    description: 'For growing teams',
    features: [
      'Unlimited invoices',
      'Advanced reporting',
      'Multiple users',
      'Priority support',
      'Custom integrations',
    ],
    cta: 'Contact Sales',
    ctaLink: '/register',
    highlighted: true,
  },
  {
    name: 'Enterprise',
    price: 'Contact us',
    period: '',
    description: 'For large organizations',
    features: [
      'Everything in Team',
      'Dedicated account manager',
      'Custom workflows',
      'API access',
      'SLA guarantee',
    ],
    cta: 'Contact Sales',
    ctaLink: '/register',
    highlighted: false,
  },
];

const BENEFITS = [
  {
    title: 'Instant visibility into outstanding & overdue',
    description: 'See exactly what\'s owed and what\'s overdue at a glance',
  },
  {
    title: 'Invoice aging by due date',
    description: 'Track aging buckets (0-30, 31-60, 61-90, 90+ days) automatically',
  },
  {
    title: 'Upload clients & invoices via CSV in minutes',
    description: 'No manual data entry—bulk import your entire receivables in seconds',
  },
  {
    title: 'Identify top overdue invoices and customer exposure',
    description: 'Prioritize collections efforts with actionable insights',
  },
  {
    title: 'Shareable reporting for finance and business leaders',
    description: 'Keep stakeholders informed with comprehensive dashboards',
  },
  {
    title: 'Built for construction and trade contractors',
    description: 'Purpose-built workflows for the unique needs of contractors',
  },
];

const Landing = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="font-heading text-2xl font-bold text-slate-900">PayForeman</h1>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={() => navigate('/login')}
                className="rounded-none border-slate-300 hover:bg-slate-50"
              >
                Log In
              </Button>
              <Button
                onClick={() => navigate('/register')}
                className="rounded-none bg-slate-900 hover:bg-slate-800 text-white"
              >
                Register
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
            Invoice Visibility & Collections Workflow Dashboard
          </h2>
          <p className="text-xl md:text-2xl text-slate-600 mb-8 max-w-3xl mx-auto">
            PayForeman is an invoice visibility and collections workflow dashboard that helps teams track outstanding balances, overdue invoices, and aging—without spreadsheets.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate('/register')}
              className="rounded-none bg-slate-900 hover:bg-slate-800 text-white h-12 px-8 text-lg"
            >
              Get Started Free
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/login')}
              className="rounded-none border-slate-300 hover:bg-slate-50 h-12 px-8 text-lg"
            >
              Log In
            </Button>
          </div>
          <p className="text-sm text-slate-500 mt-6">
            Metrics based on invoice and recorded payment data. Accounting and cash forecasting integrations coming soon.
          </p>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h3 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Why PayForeman?
            </h3>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Built specifically for contractors who need better visibility into their receivables
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {BENEFITS.map((benefit, index) => (
              <Card key={index} className="border border-slate-200 rounded-none shadow-none hover:shadow-md transition-shadow">
                <CardHeader className="pb-3">
                  <CardTitle className="font-heading text-lg font-semibold text-slate-900">
                    {benefit.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-600">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h3 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Simple, Transparent Pricing
            </h3>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Choose the plan that's right for your business
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {PRICING_TIERS.map((tier, index) => (
              <Card
                key={index}
                className={`border rounded-none shadow-none ${
                  tier.highlighted
                    ? 'border-slate-900 shadow-lg ring-2 ring-slate-900'
                    : 'border-slate-200'
                }`}
              >
                <CardHeader className="border-b border-slate-100 p-6 bg-white">
                  <CardTitle className="font-heading text-2xl font-bold text-slate-900">
                    {tier.name}
                  </CardTitle>
                  <div className="mt-4">
                    <span className="text-4xl font-bold text-slate-900">{tier.price}</span>
                    <span className="text-slate-600 ml-2">{tier.period}</span>
                  </div>
                  <p className="text-sm text-slate-600 mt-2">{tier.description}</p>
                </CardHeader>
                <CardContent className="p-6">
                  <ul className="space-y-3 mb-6">
                    {tier.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-slate-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    onClick={() => navigate(tier.ctaLink)}
                    className={`w-full rounded-none h-11 ${
                      tier.highlighted
                        ? 'bg-slate-900 hover:bg-slate-800 text-white'
                        : 'bg-slate-100 hover:bg-slate-200 text-slate-900'
                    }`}
                  >
                    {tier.cta}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 px-4 sm:px-6 lg:px-8 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="font-heading text-3xl md:text-4xl font-bold mb-6">
            Ready to streamline your collections?
          </h3>
          <p className="text-lg text-slate-300 mb-8">
            Join contractors who are modernizing their receivables management with PayForeman
          </p>
          <Button
            onClick={() => navigate('/register')}
            className="rounded-none bg-white hover:bg-slate-100 text-slate-900 h-12 px-8 text-lg"
          >
            Get Started Free
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 lg:px-8 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-center md:text-left">
              <p className="font-heading text-lg font-semibold text-slate-900">PayForeman</p>
              <p className="text-sm text-slate-600">© 2025 Ardito Advisors LLC. All rights reserved.</p>
            </div>
            <div className="flex flex-col md:flex-row gap-4 md:gap-6 items-center text-sm text-slate-600">
              <a href="mailto:support@arditoadvisors.com" className="hover:text-slate-900">
                support@arditoadvisors.com
              </a>
              <a href="/privacy" className="hover:text-slate-900">
                Privacy Policy
              </a>
              <a href="/terms" className="hover:text-slate-900">
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
